import { ArrowRight, RotateCcw, Check } from "lucide-react";
import { Reveal } from "./Reveal";

const COPY = {
  en: {
    title: "The same job, two ways.",
    before: "Before — carried by hand",
    after: "After — one record, flowing",
    beforeStages: ["Estimate", "Text to the tech", "Paper work order", "Invoice from a photo"],
    afterStages: ["Estimate", "Work order", "Completion + signature", "Invoice drafted"],
    beforeNote: "Every arrow is a person re-typing something that already exists.",
    afterNote: "Every stage reads and writes the same record. Nobody carries anything.",
    caption: "Illustrative workflow — simplified on purpose.",
    retyped: "re-typed",
  },
  fr: {
    title: "Le même contrat, deux façons.",
    before: "Avant — transporté à la main",
    after: "Après — un seul dossier qui coule",
    beforeStages: ["Soumission", "Texto au technicien", "Bon de travail papier", "Facture d'après une photo"],
    afterStages: ["Soumission", "Bon de travail", "Complétion + signature", "Facture préparée"],
    beforeNote: "Chaque flèche est une personne qui ressaisit quelque chose qui existe déjà.",
    afterNote: "Chaque étape lit et écrit le même dossier. Personne ne transporte rien.",
    caption: "Flux illustratif — simplifié exprès.",
    retyped: "ressaisi",
  },
  es: {
    title: "El mismo trabajo, dos maneras.",
    before: "Antes — transportado a mano",
    after: "Después — un solo expediente que fluye",
    beforeStages: ["Cotización", "Texto al técnico", "Orden en papel", "Factura desde una foto"],
    afterStages: ["Cotización", "Orden de trabajo", "Cierre + firma", "Factura preparada"],
    beforeNote: "Cada flecha es una persona reescribiendo algo que ya existe.",
    afterNote: "Cada etapa lee y escribe el mismo expediente. Nadie transporta nada.",
    caption: "Flujo ilustrativo — simplificado a propósito.",
    retyped: "reescrito",
  },
};

export default function BeforeAfterJobFlow({ lang = "en" }) {
  const t = COPY[lang] || COPY.en;
  return (
    <Reveal>
      <div data-testid="before-after-job-flow" className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-9">
        <h3 className="font-display font-bold tracking-tight text-brand-navy text-2xl sm:text-3xl mb-8">{t.title}</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Before */}
          <div className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-5 sm:p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-gold font-semibold mb-5">{t.before}</p>
            <div className="space-y-0">
              {t.beforeStages.map((s, i) => (
                <div key={s}>
                  <div className="rounded-xl border border-slate-300 bg-white px-4 py-3">
                    <p className="font-display font-medium text-sm text-slate-700">{s}</p>
                  </div>
                  {i < t.beforeStages.length - 1 && (
                    <div className="flex items-center gap-2 py-1.5 pl-4" aria-hidden="true">
                      <RotateCcw size={12} className="text-brand-gold" />
                      <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-brand-gold font-semibold">{t.retyped}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs text-slate-500 leading-relaxed border-t border-slate-200 pt-4">{t.beforeNote}</p>
          </div>
          {/* After */}
          <div className="rounded-2xl bg-brand-navy p-5 sm:p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-teal font-semibold mb-5">{t.after}</p>
            <div className="space-y-0">
              {t.afterStages.map((s, i) => (
                <div key={s}>
                  <div className="rounded-xl bg-brand-navysurf border border-white/10 px-4 py-3 flex items-center justify-between">
                    <p className="font-display font-medium text-sm text-white">{s}</p>
                    <Check size={14} className="text-brand-teal shrink-0" />
                  </div>
                  {i < t.afterStages.length - 1 && (
                    <div className="flex justify-center py-1" aria-hidden="true">
                      <ArrowRight size={13} className="text-brand-teal rotate-90" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs text-slate-400 leading-relaxed border-t border-white/10 pt-4">{t.afterNote}</p>
          </div>
        </div>
        <p className="mt-5 text-[11px] text-slate-400 text-center italic">{t.caption}</p>
      </div>
    </Reveal>
  );
}
